<template>
  <UContainer>
    <section class="max-w-2xl mx-auto py-32">
      <h2
        class="text-center mb-7 text-4xl text-(--main-color) dark:text-(--main-color-400) font-semibold"
      >
        Inscription
      </h2>

      <div
        class="border border-(--main-color) dark:border-(--main-color-400) rounded-lg"
      >
        <UForm
          :schema="schema"
          :state="state"
          class="space-y-5 login-form p-5"
          @submit="registerUser"
        >
          <UFormField label="Email" name="email">
            <UInput
              v-model="state.email"
              class="w-full"
              color="main"
              icon="i-lucide-mail"
              size="lg"
            />
          </UFormField>
          <UFormField label="Nom d'utilisateur" name="username">
            <UInput
              v-model="state.username"
              class="w-full"
              color="main"
              icon="i-lucide-user"
              size="lg"
            />
          </UFormField>
          <UFormField label="Nom" name="last_name">
            <UInput
              v-model="state.last_name"
              class="w-full"
              color="main"
              icon="i-lucide-id-card"
              size="lg"
            />
          </UFormField>
          <UFormField label="Prenom" name="first_name">
            <UInput
              v-model="state.first_name"
              class="w-full"
              color="main"
              icon="i-lucide-pen"
              size="lg"
            />
          </UFormField>
          <UFormField label="Numero de telephone" name="phone_number">
            <UInput
              v-model="state.phone_number"
              class="w-full"
              color="main"
              icon="i-lucide-phone"
              size="lg"
            />
          </UFormField>
          <UFormField label="Mot de passe" name="password">
            <UInput
              v-model="state.password"
              :type="showPassword ? 'text' : 'password'"
              :ui="{ trailing: 'pe-1' }"
              class="w-full"
              color="main"
              icon="i-lucide-lock"
              size="lg"
            >
              <template #trailing>
                <UButton
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                  :aria-pressed="showPassword"
                  :icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                  aria-controls="password"
                  color="neutral"
                  size="sm"
                  variant="link"
                  @click="showPassword = !showPassword"
                />
              </template>
            </UInput>
          </UFormField>
          <UProgress
            :color="color"
            :indicator="text"
            :max="5"
            :model-value="score"
            size="sm"
          />
          <p id="password-strength" class="text-sm font-medium">
            {{ text }}. <br />Le mot de passe doit respecter les conditions
            suivantes:
          </p>

          <ul aria-label="Password requirements" class="space-y-1">
            <li
              v-for="(req, index) in strength"
              :key="index"
              :class="
                req.met ? 'text-(--ui-success)' : 'text-(--ui-text-muted)'
              "
              class="flex items-center gap-0.5"
            >
              <UIcon
                :name="req.met ? 'i-lucide-circle-check' : 'i-lucide-circle-x'"
                class="size-4 shrink-0"
              />
              <span class="text-sm font-light">
                {{ req.text }}
                <span class="sr-only">
                  {{
                    req.met ? " - Requirement met" : " - Requirement not met"
                  }}
                </span>
              </span>
            </li>
          </ul>
          <UFormField label="Confirmation mot de passe" name="password_confirm">
            <UInput
              v-model="state.password_confirm"
              :type="showConfirmPassword ? 'text' : 'password'"
              :ui="{ trailing: 'pe-1' }"
              class="w-full"
              color="main"
              icon="i-lucide-lock"
              size="lg"
            >
              <template #trailing>
                <UButton
                  :aria-label="
                    showConfirmPassword ? 'Hide password' : 'Show password'
                  "
                  :aria-pressed="showConfirmPassword"
                  :icon="
                    showConfirmPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'
                  "
                  aria-controls="password"
                  color="neutral"
                  size="sm"
                  variant="link"
                  @click="showConfirmPassword = !showConfirmPassword"
                />
              </template>
            </UInput>
          </UFormField>
          <UProgress
            :color="
              state.password === state.password_confirm &&
              state.password_confirm != ''
                ? 'success'
                : 'error'
            "
            :indicator="text"
            :max="1"
            :model-value="state.password === state.password_confirm ? 1 : 0"
            size="sm"
          />
          <UButton
            :disabled="pending"
            :loading="pending"
            class="text-white w-full justify-center"
            color="main"
            label="S'inscrire"
            size="lg"
            type="submit"
          />
        </UForm>
      </div>
    </section>
  </UContainer>
</template>

<script lang="ts" setup>
import { z } from "zod";
import type { FormSubmitEvent } from "#ui/types";

const { $api } = useNuxtApp();
const router = useRouter();

const showPassword = ref(false);
const showConfirmPassword = ref(false);
const pending = ref(false);

const upperCase = /(?=.*[A-Z])/;
const lowerCase = /(?=.*[a-z])/;
const onlyNumber = /(?=.*[0-9])/;
const specialChars = /(?=.*[#?!@$%^&*-])/;
const phone = /^0\d{9}$/;
const noSpecialChars = /^[A-Za-z-\s\d]+$/;
const letters = /^[A-Za-z-\s]+$/;

function checkStrength(str: string) {
  const requirements = [
    { regex: /.{12,}/, text: "Au moins 12 charactere" },
    { regex: /\d/, text: "Au moins 1 chiffre" },
    { regex: /[a-z]/, text: "Au moins 1 charactere minuscule" },
    { regex: /[A-Z]/, text: "Au moins 1 charactere majuscule" },
    {
      regex: /[#?!@$%^&*-]/,
      text: "Au moins 1 charactere spécial (#?!@$%^&*-)",
    },
  ];

  return requirements.map((req) => ({
    met: req.regex.test(str),
    text: req.text,
  }));
}

const strength = computed(() => checkStrength(state.password));
const score = computed(() => strength.value.filter((req) => req.met).length);

const color = computed(() => {
  if (score.value === 0) return "neutral";
  if (score.value <= 2) return "error";
  if (score.value <= 4) return "warning";
  return "success";
});

const text = computed(() => {
  if (score.value <= 4) return "Mot de passe non valide";
  return "Mot de passe fort";
});

const schema = z
  .object({
    email: z.string({ message: "Champs vide" }).email("Email invalide"),
    username: z
      .string({ message: "Champs vide" })
      .min(4, "Vous devez saisir au minimum 4 caractere")
      .regex(
        noSpecialChars,
        "Vous ne pouvez saisir que des lettres et des chiffres"
      ),
    first_name: z
      .string({ message: "Champs vide" })
      .min(4, "Vous devez saisir au minimum 4 caractere")
      .regex(letters, "Vous ne pouvez saisir que des lettres"),
    last_name: z
      .string({ message: "Champs vide" })
      .min(4, "Vous devez saisir au minimum 4 caractere")
      .regex(letters, "Vous ne pouvez saisir que des lettres"),
    phone_number: z
      .string({ message: "Champs vide" })
      .length(10, "Un numero contient 10 chiffre")
      .regex(
        phone,
        "Vous devez saisir un numero de telephone valide 0XXXXXXXXX"
      ),
    password: z
      .string({ message: "Champs vide" })
      .min(12, "Le mot de passe doit contenir au minimum 12 caracteres")
      .regex(
        upperCase,
        "Le mot de passe doit contenir au minimum une majuscule"
      )
      .regex(
        lowerCase,
        "Le mot de passe doit contenir au minimum une minuscule"
      )
      .regex(onlyNumber, "Le mot de passe doit contenir au minimum un chiffre")
      .regex(
        specialChars,
        "Le mot de passe doit contenir au minimum l'un des caractere suivant: #?!@$%^&*-"
      ),
    password_confirm: z
      .string({ message: "Champs vide" })
      .min(12, "Le mot de passe doit contenir au minimum 12 caracteres")
      .regex(
        upperCase,
        "Le mot de passe doit contenir au minimum une majuscule"
      )
      .regex(
        lowerCase,
        "Le mot de passe doit contenir au minimum une minuscule"
      )
      .regex(onlyNumber, "Le mot de passe doit contenir au minimum un chiffre")
      .regex(
        specialChars,
        "Le mot de passe doit contenir au minimum l'un des caractere suivant: #?!@$%^&*-"
      ),
  })
  .refine((data) => data.password === data.password_confirm, {
    message:
      "Les mots de passes ne sont pas identiques, veuillez saisir le meme mot de passe",
    path: ["password_confirm"],
  });

type Schema = z.output<typeof schema>;

const state = reactive({
  email: "",
  password: "",
  password_confirm: "",
  username: "",
  first_name: "",
  last_name: "",
  phone_number: "",
});

async function registerUser(event: FormSubmitEvent<Schema>) {
  pending.value = true;
  try {
    console.log(event.data);

    const payload = {
      email: event.data.email,
      password: event.data.password,
      password_confirm: event.data.password_confirm,
      username: event.data.username,
      first_name: event.data.first_name,
      last_name: event.data.last_name,
      phone_number: event.data.phone_number,
    };

    await $api.post("/user/register", payload, "include");
    await router.push({ path: "/login", query: { showAlert: "true" } });
  } catch (error) {
    console.log(error);
  }
  pending.value = false;
}

definePageMeta({
  middleware: "auth",
});
</script>
